import React, { useEffect, useMemo } from 'react';
import { View, ViewStyle, StyleProp } from 'react-native';
import Svg, { G, Path } from 'react-native-svg';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  Easing,
  interpolate,
} from 'react-native-reanimated';
import { getStyles } from './IconPayStatus.styles';
import { useTheme } from '../../hooks/useTheme';

interface Props {
  status: 'default' | 'error';
  containerStyle?: StyleProp<ViewStyle>;
}

const IconPayStatus: React.FC<Props> = ({ status, containerStyle }) => {
  const { colors } = useTheme();
  const styles = useMemo(() => getStyles(colors), [colors]);

  const isError = status === 'error';

  const progress = useSharedValue(0);

  useEffect(() => {
    progress.value = withRepeat(
      withTiming(1, { duration: 500, easing: Easing.out(Easing.ease) }),
      -1,
      true,
    );
  }, [progress]);

  const animatedCardStyle = useAnimatedStyle(() => {
    const right = interpolate(progress.value, [0, 1], [-60, -40]);
    const rotate = interpolate(progress.value, [0, 1], [10, 15]);
    return {
      right,
      transform: [{ rotate: `${rotate}deg` }],
    };
  }, [progress]);

  return (
    <View style={[styles.container, containerStyle]}>
      <View style={styles.pad}>
        <View style={[styles.status, isError && styles.statusError]} />
        {isError ? (
          <Svg width={140} height={116} viewBox="0 0 140 116">
            <G stroke="none" strokeWidth={1} fill="none" fillRule="evenodd">
              <G transform="translate(-30, -54)">
                <G transform="translate(32, 56)">
                  <G
                    opacity={0.2}
                    stroke={colors.icon}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                  >
                    <Path
                      d="M136,82 L114,82 C109.581722,82 106,85.581722 106,90 L106,112 L106,112"
                      transform="translate(121, 97) scale(-1, -1) translate(-121, -97)"
                    />
                    <Path
                      d="M30,82 L8,82 C3.581722,82 -8.11624501e-16,85.581722 0,90 L0,112 L0,112"
                      transform="translate(15, 97) scale(1, -1) translate(-15, -97)"
                    />
                    <Path
                      d="M136,0 L114,0 C109.581722,8.11624501e-16 106,3.581722 106,8 L106,30 L106,30"
                      transform="translate(121, 15) scale(-1, 1) translate(-121, -15)"
                    />
                    <Path d="M30,0 L8,0 C3.581722,8.11624501e-16 -8.11624501e-16,3.581722 0,8 L0,30 L0,30" />
                  </G>
                  <Path
                    d="M68,42 C75.6761364,42 82,48.3208708 82,55.9933743 C82,63.6791292 75.6893939,70 68,70 C60.3238636,70 54,63.6791292 54,55.9933743 C54,48.3208708 60.3106061,42 68,42 Z M68.0132576,59.4122101 C66.9526515,59.4122101 66.1306818,60.0880265 66.1306818,61.0818741 C66.1306818,62.1022243 66.9526515,62.7647894 68.0132576,62.7647894 C69.0606061,62.7647894 69.8693182,62.1022243 69.8693182,61.0818741 C69.8693182,60.0880265 69.0738636,59.4122101 68.0132576,59.4122101 Z M68,49.1159489 C66.9393939,49.1159489 66.2234848,49.7917653 66.2765152,50.7988642 L66.4886364,56.5234264 C66.5284091,57.4642688 67.0719697,57.9810696 68.0132576,57.9810696 C68.9280303,57.9810696 69.4583333,57.4642688 69.5113636,56.5101751 L69.7234848,50.7988642 C69.7765152,49.8050166 69.0473485,49.1159489 68,49.1159489 Z"
                    fill={colors.negative}
                    fillRule="nonzero"
                  />
                </G>
              </G>
            </G>
          </Svg>
        ) : (
          <Svg width={140} height={116} viewBox="0 0 140 116">
            <G
              stroke="none"
              strokeWidth={1}
              fill="none"
              fillRule="evenodd"
              opacity={0.2}
            >
              <G transform="translate(-30, -54)">
                <G transform="translate(32, 56)">
                  <G
                    stroke={colors.icon}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                  >
                    <Path
                      d="M136,82 L114,82 C109.581722,82 106,85.581722 106,90 L106,112 L106,112"
                      transform="translate(121, 97) scale(-1, -1) translate(-121, -97)"
                    />
                    <Path
                      d="M30,82 L8,82 C3.581722,82 -8.11624501e-16,85.581722 0,90 L0,112 L0,112"
                      transform="translate(15, 97) scale(1, -1) translate(-15, -97)"
                    />
                    <Path
                      d="M136,0 L114,0 C109.581722,8.11624501e-16 106,3.581722 106,8 L106,30 L106,30"
                      transform="translate(121, 15) scale(-1, 1) translate(-121, -15)"
                    />
                    <Path d="M30,0 L8,0 C3.581722,8.11624501e-16 -8.11624501e-16,3.581722 0,8 L0,30 L0,30" />
                  </G>
                  <Path
                    d="M67.9933743,70 C69.9192302,70 71.7258243,69.6355892 73.4131566,68.9067676 C75.100489,68.177946 76.5868434,67.1686386 77.8722196,65.8788452 C79.1575958,64.5890519 80.1669033,63.100489 80.900142,61.4131566 C81.6333807,59.7258243 82,57.9192302 82,55.9933743 C82,54.0763527 81.6333807,52.2741757 80.900142,50.5868434 C80.1669033,48.899511 79.1575958,47.4109481 77.8722196,46.1211548 C76.5868434,44.8313614 75.0982805,43.822054 73.406531,43.0932324 C71.7147815,42.3644108 69.910396,42 67.9933743,42 C66.0763527,42 64.2719672,42.3644108 62.5802177,43.0932324 C60.8884682,43.822054 59.3999053,44.8313614 58.1145291,46.1211548 C56.8291529,47.4109481 55.822054,48.899511 55.0932324,50.5868434 C54.3644108,52.2741757 54,54.0763527 54,55.9933743 C54,57.9192302 54.3644108,59.7258243 55.0932324,61.4131566 C55.822054,63.100489 56.8313614,64.5890519 58.1211548,65.8788452 C59.4109481,67.1686386 60.899511,68.177946 62.5868434,68.9067676 C64.2741757,69.6355892 66.0763527,70 67.9933743,70 Z M65.5418836,56.0066257 C65.5418836,56.6956933 65.4424988,57.3670926 65.2437293,58.0208235 C65.0449598,58.6745543 64.7600568,59.2487774 64.3890204,59.7434927 C64.2123363,60.0085187 63.9583531,60.17416 63.6270705,60.2404165 C63.295788,60.306673 62.9843824,60.2470421 62.6928538,60.0615239 C62.3659883,59.8583373 62.176053,59.5778514 62.1230478,59.2200663 C62.0700426,58.8622811 62.1583846,58.4978703 62.3880738,58.1268339 C62.7856129,57.5261082 62.9843824,56.8193721 62.9843824,56.0066257 C62.9843824,55.185045 62.7856129,54.482726 62.3880738,53.8996687 C62.1495504,53.5286323 62.0589998,53.1620129 62.1164221,52.7998107 C62.1738445,52.4376085 62.3659883,52.154914 62.6928538,51.9517274 C62.9843824,51.7750434 63.2935794,51.7176211 63.6204449,51.7794605 C63.9473103,51.8412999 64.2035021,52.0091497 64.3890204,52.2830099 C65.1575958,53.3431141 65.5418836,54.5843193 65.5418836,56.0066257 Z M69.7955513,56.0066257 C69.7955513,57.0843982 69.6453699,58.1268339 69.3450071,59.1339328 C69.0446443,60.1410317 68.6073513,61.0642057 68.0331283,61.9034548 C67.8387758,62.2038176 67.5781669,62.3915444 67.2513015,62.4666351 C66.924436,62.5417258 66.6064048,62.482095 66.2972078,62.2877425 C65.9880107,62.1022243 65.800284,61.8283641 65.7340274,61.4661619 C65.6677709,61.1039596 65.7450702,60.7461745 65.9659252,60.3928064 C66.3722985,59.7832466 66.6859126,59.1030131 66.9067676,58.352106 C67.1276227,57.6011989 67.2380502,56.8193721 67.2380502,56.0066257 C67.2380502,55.2027134 67.1276227,54.4253037 66.9067676,53.6743966 C66.6859126,52.9234895 66.3722985,52.243256 65.9659252,51.6336962 C65.7450702,51.2714939 65.6677709,50.9115002 65.7340274,50.5537151 C65.800284,50.19593 65.9880107,49.9198612 66.2972078,49.7255088 C66.6064048,49.5399905 66.924436,49.4847768 67.2513015,49.5598675 C67.5781669,49.6349582 67.8387758,49.822685 68.0331283,50.1230478 C68.6073513,50.9534627 69.0446443,51.8722196 69.3450071,52.8793185 C69.6453699,53.8864174 69.7955513,54.9288531 69.7955513,56.0066257 Z M74.0227165,56.0066257 C74.0227165,57.4642688 73.8217384,58.8843666 73.4197823,60.2669191 C73.0178262,61.6494715 72.4369774,62.9282221 71.6772362,64.1031708 C71.4828837,64.4123679 71.2156492,64.6067203 70.8755324,64.6862281 C70.5354157,64.7657359 70.2151759,64.7083136 69.9148131,64.5139612 C69.5967818,64.3196088 69.4068465,64.0479571 69.3450071,63.6990062 C69.2831677,63.3500552 69.3538413,63.0033128 69.5570279,62.658779 C70.1842562,61.6958511 70.661303,60.6467897 70.9881685,59.5115949 C71.3150339,58.3764001 71.4784666,57.208077 71.4784666,56.0066257 C71.4784666,54.7963401 71.3150339,53.628017 70.9881685,52.5016564 C70.661303,51.3752958 70.1842562,50.3306515 69.5570279,49.3677236 C69.3538413,49.0231898 69.2831677,48.6764474 69.3450071,48.3274965 C69.4068465,47.9785455 69.5967818,47.7068938 69.9148131,47.5125414 C70.2151759,47.318189 70.5354157,47.2607667 70.8755324,47.3402745 C71.2156492,47.4197823 71.4828837,47.6141347 71.6772362,47.9233318 C72.4369774,49.1071147 73.0178262,50.3880738 73.4197823,51.7662092 C73.8217384,53.1443445 74.0227165,54.5578167 74.0227165,56.0066257 Z"
                    fill={colors.icon}
                    fillRule="nonzero"
                  />
                </G>
              </G>
            </G>
          </Svg>
        )}
      </View>
      <Animated.View style={[styles.card, animatedCardStyle]}>
        <View style={[styles.foreground, isError && styles.foregroundError]}>
          <View style={styles.chip} />
        </View>
        <View style={styles.shadow} />
      </Animated.View>
    </View>
  );
};

export default IconPayStatus;
